<template lang="pug">
span.icon(aria-hidden="true" :style="iconStyle" :class="{ 'safari-loading': isSafariDesktop && !spriteLoaded && INLINE_SPRITES.has(sprite) }")
  svg(aria-hidden="true")
    use(:href="href")
</template>
<script setup lang="ts">
import { ref, watch, computed, inject } from 'vue'
import { useRogue } from '../../js/rogue'

const rogue = useRogue()
  
type Props = {
  /** The icon to display: `<icon>` for the base sprite, or `<sprite>/<icon>` (e.g. `flags/US`) */
  name: string
  /** The fill color of the SVG icon. Can either use a design token or a raw color */
  fill?: string
}

const props = withDefaults(defineProps<Props>(), {
  fill: 'inherit',
})
  
const COLORS: Record<string, string> = {
  green     : '#5BBD15',
  red       : '#EA0000',
  gray_dark : '#A2A2A2',
}

const baseUrl = inject<string>('rogue-icon-base-url', '/')

/**
 * Optional `{ spriteName: url }` map of cache-busted sprite URLs (e.g. `/svc_spa/sprites/base.1a2b3c4d.svg`).
 * Apps that don't provide it (e.g. m2-rogue-builder) keep using `${baseUrl}assets/<sprite>.svg`.
 */
const spriteUrls = inject<Record<string, string> | null>('rogue-icon-sprites', null)

/** URL of a sprite file, e.g. `base` -> `/svc_spa/sprites/base.1a2b3c4d.svg` or `/assets/base.svg` */
const spritePath = (name: string): string => spriteUrls?.[name] || `${baseUrl}assets/${name}.svg`
  
const INLINE_SPRITES = new Set(['cc', 'media'])

// Detect Safari desktop (not iOS)
const isSafariDesktop = typeof window !== 'undefined' && 
  /^((?!chrome|android).)*safari/i.test(navigator.userAgent) &&
  !/iPad|iPhone|iPod/.test(navigator.userAgent)

// Track loaded inline sprites globally across all icon instances
const loadedSprites = new Set<string>()
  
const sprite = ref<string>('base')
const icon = ref<string>('')
const spriteLoaded = ref<boolean>(false)
  
const fillColor = computed<string>(() => COLORS[props.fill] || props.fill)
const iconStyle = computed<Record<string, string> | undefined>(() =>
  fillColor.value && fillColor.value !== 'inherit' ? { color: fillColor.value } : undefined
)
  
const href = computed<string>(() => {
  const isInline = INLINE_SPRITES.has(sprite.value)
  const isLoaded = spriteLoaded.value || loadedSprites.has(sprite.value)
  
  // Safari desktop MUST wait for inline sprite (external doesn't work)
  // We hide the icon with CSS until loaded to avoid white icons
  if (isSafariDesktop && isInline) {
    return `#sprite-${icon.value}`
  }
  
  // Mobile iOS and other browsers: start with external, switch to inline when loaded
  return (isInline && isLoaded)
    ? `#sprite-${icon.value}`
    : `${spritePath(sprite.value)}#sprite-${icon.value}`
})
  
function injectInlineSprite() {
  if (rogue?.ssr || !INLINE_SPRITES.has(sprite.value)) return

  const spriteName = sprite.value
  const id = `rg-sprite-${spriteName}`
  
  // If already loaded globally, mark this instance as loaded
  if (loadedSprites.has(spriteName)) {
    spriteLoaded.value = true
    
    return
  }
  
  // If DOM element already exists, mark as loaded
  if (document.getElementById(id)) {
    loadedSprites.add(spriteName)
    spriteLoaded.value = true
    
    return
  }

  const div = document.createElement('div')
  div.id = id
  div.className = 'rg-icon-sprite'
  document.body.insertBefore(div, document.body.firstChild)

  const ajax = new XMLHttpRequest()
  ajax.open('GET', spritePath(spriteName), true)
  ajax.send()
  ajax.onload = () => {
    div.innerHTML = ajax.responseText
    // Mark as globally loaded so other icon instances know
    loadedSprites.add(spriteName)
    spriteLoaded.value = true
  }
}
  
watch(
  () => props.name,
  (newName) => {
    const [maybeSprite, maybeIcon] = newName.split('/')
    sprite.value = maybeIcon ? maybeSprite : 'base'
    icon.value = maybeIcon || maybeSprite
    injectInlineSprite()
  },
  { immediate: true }
)
</script>

<style lang="scss">
// We don’t want to use scoped since these styles need to cascade down to SVGs.
// We also want to be able to style .icon inside buttons etc.
.rg-icon-sprite {
  // ! Cant be display:none or else defs for gradients dont work
  visibility: hidden;
  overflow: hidden;
  height: 0;
  width: 0;
}

.icon {
  display: flex;
  margin: 0;
  padding: 0;
  height: $size-m;
  width: $size-m;

  svg {
    align-self: center;
    vertical-align: unset;
    pointer-events: none;
    width: 100%;
    height: 100%;
    fill: currentColor;
  }

  // Ensure use elements inherit the fill
  use {
    fill: inherit;
  }
  
  // Hide Safari desktop icons until sprite is loaded
  &.safari-loading {
    opacity: 0;
  }
}
</style>
